import EmptyState from "@/components/empty-state";
import Spinner from "@/components/spinner";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useApplyForEdition } from "@/hooks/apply-for-edition.hook";
import { useEditionById } from "@/hooks/edition-by-id.hook";
import { useResourceLibrary } from "@/hooks/resource-library.hook";
import { ApplyForEditionRequestForm } from "@/requests/applications/apply-for-edition.request";
import { InscriptionFormType } from "@/types/edition";
import { ArrowLeft, DocumentText } from "iconsax-react";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import EnrollmentFileUploader from "./enrollment-file-uploader";

type ApplicationAttachments = Partial<
  Record<InscriptionFormType, ApplyForEditionRequestForm>
>;

const EnrollmentForm = () => {
  const { editionId } = useParams();

  const { data: resourceLibrary } = useResourceLibrary();

  const { data: edition } = useEditionById({
    companyId: resourceLibrary?.owner.id || "",
    editionId: editionId || "",
  });

  const applicationMutation = useApplyForEdition();

  const [applicationAttachments, setApplicationAttachments] =
    useState<ApplicationAttachments>({});

  const handleSubmit = () => {
    if (Object.keys(applicationAttachments).length === 0) {
      toast.error("Faltan datos", {
        description: "Debes completar por lo menos una ficha",
      });
      return;
    }

    applicationMutation.mutate({
      companyId: resourceLibrary?.owner.id || "",
      editionId: edition?.id || "",
      forms: Object.entries(applicationAttachments).map(([, attachment]) => ({
        resourceId: attachment.resourceId,
        formType: attachment.formType,
        name: attachment.name,
      })),
    });
  };

  return (
    <div>
      <Alert className="my-6 flex gap-2 border-[#DEE3E7]">
        <div className="shrink-0">
          <DocumentText className="mt-0.5 text-[#FFB92C]" size={20} />
        </div>
        <div className="grow">
          <AlertTitle className="text-lg">Fichas de registro</AlertTitle>
          <AlertDescription>
            Para completar tu inscripción descarga las fichas en cada sección,
            rellénalas y súbelas para ser revisadas por nuestro equipo.
          </AlertDescription>
        </div>
      </Alert>

      {edition?.inscriptionForms.length ? (
        <>
          <div className="flex flex-col gap-10">
            {edition?.inscriptionForms.map((inscriptionForm) => (
              <EnrollmentFileUploader
                key={inscriptionForm.id}
                form={inscriptionForm}
                onFileUpload={(attachment) => {
                  setApplicationAttachments((prev) => ({
                    ...prev,
                    [attachment.formType]: attachment,
                  }));
                }}
                onFileRemove={(attachment) => {
                  setApplicationAttachments((prev) => {
                    const newAttachments = { ...prev };
                    delete newAttachments[attachment.formType];
                    return newAttachments;
                  });
                }}
              />
            ))}
          </div>

          <div className="mt-12 flex justify-center gap-2 text-center">
            <Button variant={"secondary"} asChild>
              <Link to="/">
                <ArrowLeft />
                Volver
              </Link>
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={applicationMutation.isPending}
            >
              {applicationMutation.isPending && <Spinner className="mr-2" />}
              Enviar registro
            </Button>
          </div>
        </>
      ) : (
        <div className="text-center">
          <EmptyState
            className="mx-auto my-12"
            message="Parece que no hay fichas de registro para esta edición. Comunícate con un administrador de Empresa con Gestión Sostenible para recibir ayuda."
          />

          <Button variant={"secondary"} size={"sm"} asChild>
            <Link to="/">
              <ArrowLeft />
              Volver
            </Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default EnrollmentForm;
